<template>
  <div class="mt-6 bg-base-100 rounded-lg p-6">
    <div>
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-xl font-semibold">
          {{
            isEditing
              ? "Redaguoti transporto priemonę"
              : "Nauja transporto priemonė"
          }}
        </h2>
        <button
          v-if="isEditing"
          @click="confirmDelete(formData.id)"
          class="btn btn-sm btn-ghost"
          title="Ištrinti"
        >
          <DefaultIcon name="trash" size="w-5 h-5" />
        </button>
      </div>

      <form @submit.prevent="handleSubmit" class="space-y-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- Registration Number -->
          <fieldset class="fieldset w-full">
            <legend class="fieldset-legend">Valstybinis numeris</legend>
            <input
              v-model="formData.reg_nr"
              type="text"
              class="input w-full"
              required
            />
          </fieldset>

          <!-- Model -->
          <fieldset class="fieldset w-full">
            <legend class="fieldset-legend">Modelis</legend>
            <input
              v-model="formData.model"
              type="text"
              class="input w-full"
              required
            />
          </fieldset>

          <!-- Type -->
          <fieldset class="fieldset w-full">
            <legend class="fieldset-legend">Tipas</legend>
            <select
              v-model="formData.type"
              class="select w-full"
              required
            >
              <option value="" disabled>Pasirinkite tipą</option>
              <option
                v-for="(label, value) in vehicleTypes"
                :key="value"
                :value="value"
              >
                {{ label }}
              </option>
            </select>
          </fieldset>

          <!-- Address -->
          <AddressSearchInput
            v-model="formData.address"
            :required="true"
            @coordinates-change="handleCoordinatesChange"
            class="w-full"
          />

          <!-- Seats Section -->
          <div class="grid grid-cols-2 gap-4">
            <fieldset class="fieldset">
              <legend class="fieldset-legend">Įprastos sėdimos vietos</legend>
              <input
                v-model="formData.seats_regular"
                type="number"
                class="input w-full"
                required
              />
            </fieldset>

            <fieldset class="fieldset">
              <legend class="fieldset-legend">Neįgaliųjų sėdimos vietos</legend>
              <input
                v-model="formData.seats_disabled"
                type="number"
                class="input w-full"
                required
              />
            </fieldset>
          </div>

          <!-- Vehicle Specs Section -->
          <div class="grid grid-cols-2 gap-4">
            <fieldset class="fieldset">
              <legend class="fieldset-legend">Kuro sąnaudos</legend>
              <input
                v-model="formData.fuel_consumption"
                type="number"
                step="0.1"
                class="input w-full"
                required
              />
            </fieldset>

            <fieldset class="fieldset">
              <legend class="fieldset-legend">Maksimalus greitis</legend>
              <input
                v-model="formData.top_speed"
                type="number"
                step="0.1"
                class="input w-full"
                required
              />
            </fieldset>
          </div>

          <!-- Description -->
          <fieldset class="fieldset">
            <legend class="fieldset-legend">Aprašymas</legend>
            <textarea
              v-model="formData.description"
              class="textarea h-24 w-full"
              required
            ></textarea>
          </fieldset>

          <!-- Image URL -->
          <fieldset class="fieldset">
            <legend class="fieldset-legend">Nuotraukos URL</legend>
            <input
              v-model="formData.img_url"
              type="url"
              class="input w-full"
              required
            />
            <div v-if="formData.img_url" class="mt-2">
              <img
                :src="formData.img_url"
                alt="Preview"
                class="max-w-xs rounded-sm"
              />
            </div>
          </fieldset>
        </div>

        <!-- Form Actions -->
        <div class="flex justify-end gap-4 mt-8">
          <button type="button" class="btn btn-ghost" @click="goBack('/vehicles')">
            Atšaukti
          </button>
          <button
            type="submit"
            class="btn btn-success"
            :disabled="vehicleStore.isLoading"
          >
            {{ isEditing ? "Atnaujinti" : "Pridėti" }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { useVehicleStore } from "@/stores/vehicles";
import { ref, onMounted, computed } from "vue";
import { useRoute } from "vue-router";
import { useGoBack } from "@/composables/navigation/useGoBack";
import AddressSearchInput from "@/components/search/AddressSearchInput.vue";
import DefaultIcon from "@/components/icons/DefaultIcon.vue";

const route = useRoute();
const goBack = useGoBack();
const vehicleStore = useVehicleStore();

const isEditing = computed(() => !!route.params.id);

const formData = ref({
  reg_nr: "",
  model: "",
  type: "",
  description: "",
  address: "",
  lat: null,
  lng: null,
  img_url: "",
  seats_regular: 0,
  seats_disabled: 0,
  fuel_consumption: 0,
  top_speed: 80,
});

const vehicleTypes = {
  car: "Automobilis",
  van: "Mikroautobusas",
  bus: "Autobusas",
};

onMounted(async () => {
  if (isEditing.value) {
    try {
      const vehicle = await vehicleStore.fetchVehicleById(route.params.id);
      if (vehicle) {
        formData.value = { ...vehicle };
      } else {
        goBack("/vehicles");
      }
    } catch (error) {
      console.error("Failed to fetch vehicle:", error);
      goBack("/vehicles");
    }
  }
});

const handleSubmit = async () => {
  try {
    if (isEditing.value) {
      await vehicleStore.updateVehicle(route.params.id, formData.value);
    } else {
      await vehicleStore.addVehicle(formData.value);
    }
    goBack("/vehicles");
  } catch (error) {
    console.error("Failed to save vehicle:", error);
  }
};

const confirmDelete = async (id) => {
  if (confirm("Ar tikrai norite ištrinti šią transporto priemonę?")) {
    try {
      await vehicleStore.deleteVehicle(id);
      goBack("/vehicles");
    } catch (error) {
      console.error("Failed to delete vehicle:", error);
    }
  }
};

const handleCoordinatesChange = ({ lat, lng }) => {
  formData.value.lat = lat;
  formData.value.lng = lng;
};
</script>
